import { TabPanel, Tabs } from '@jobtarget/ui-library'
import React, { useState, useEffect, useRef } from 'react'
import './css/NavigationTabs.css' // turn off focus outline

export interface TabItem {
  id: string;
  label: string | React.ReactNode;
  content: React.ReactNode;
  panelId: string;
}

export interface NavigationTabsProps {
  tabs: TabItem[];
  defaultActiveTab?: number;
  activeTab?: number;
  onTabChange?: (activeTabIndex: number, tabItem: TabItem) => void;
  className?: string;
}

const NavigationTabs: React.FC<NavigationTabsProps> = ({
  tabs,
  defaultActiveTab = 0,
  activeTab,
  onTabChange,
  className
}) => {
  const [currentFocus, setCurrentFocus] = useState(activeTab !== undefined ? activeTab : defaultActiveTab);
  const tabsRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const lastActiveTabFromParent = useRef<number | undefined>(activeTab);

  // Allow parent to control active tab
  useEffect(() => {
    if (activeTab === undefined) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastActiveTabFromParent.current = activeTab;
      setCurrentFocus(activeTab);

      // On mount, sync the UI library indicator if activeTab differs from default
      if (activeTab !== defaultActiveTab) {
        setTimeout(() => {
          if (tabsRef.current) {
            const tabButtons = tabsRef.current.querySelectorAll('[role="tab"]');
            if (tabButtons[activeTab]) {
              (tabButtons[activeTab] as HTMLElement).click();
            }
          }
        }, 0);
      }
      return;
    }

    if (activeTab !== lastActiveTabFromParent.current) {
      lastActiveTabFromParent.current = activeTab;
      setCurrentFocus(activeTab);

      setTimeout(() => {
        if (tabsRef.current) {
          const tabButtons = tabsRef.current.querySelectorAll('[role="tab"]');
          if (tabButtons[activeTab]) {
            (tabButtons[activeTab] as HTMLElement).click();
          }
        }
      }, 0);
    }
  }, [activeTab, defaultActiveTab]);

  const handleTabChange = (index: number) => {
    setCurrentFocus(index);
    if (onTabChange) {
      onTabChange(index, tabs[index]);
    }
  };

  // Transform tabs for the Tabs component
  const tabsForComponent = tabs.map((tab, index) => ({
    children: (
        <span style={{ 
          color: currentFocus === index ? '#007bff' : '#6c757d',
          fontWeight: '500'
        }}>
          {tab.label}
        </span>
    ),
    id: tab.id,
    panelId: tab.panelId
  }));

  return (
    <div className={className} ref={tabsRef}>
      <Tabs
        handleChange={(obj: { id?: string | undefined; index: number; }) => {
          const tabId = obj.id;
          const index = tabs.findIndex(tab => tab.id === tabId);
          if (index !== -1) {
            handleTabChange(index);
          }
        }}
        items={tabsForComponent}
        style={{
            borderBottom: '1px solid #dee2e6',
            marginBottom: '0'
        }}
      />
      
      {tabs.map((tab, index) => (
        <TabPanel
          key={tab.panelId}
          currentFocus={currentFocus}
          id={tab.panelId}
          index={index}
          tabId={tab.id}
        >
          {tab.content}
        </TabPanel>
      ))}
    </div>
  );
};

export default NavigationTabs;